import { useState, type FormEvent, type ReactNode } from 'react';

const BUSINESS_TYPES = [
  'Kurbanlık işletmesi',
  'Adaklık işletmesi',
  'Canlı hayvan çiftliği',
  'Kasap / et satış',
  'Şube / aracı yapısı olan firma',
  'Diğer',
];

const INTEREST_AREAS = [
  'CiftlikWeb Pro demo',
  'İlk canlı kurulum',
  'Evren Çiftliği benzeri kurban operasyonu',
  'Cari portal ve kullanıcı yetkileri',
  'Alt domain / özel domain / bizim yönettiğimiz domain',
  'Excel’den geçiş / veri aktarımı',
];

async function submitLead(form: HTMLFormElement, leadType: string) {
  const data = Object.fromEntries(new FormData(form).entries());
  const payload = {
    full_name: String(data.full_name || '').trim(),
    company_name: String(data.company_name || '').trim(),
    email: String(data.email || '').trim(),
    phone: String(data.phone || '').trim(),
    city: String(data.city || '').trim(),
    business_type: String(data.business_type || '').trim(),
    interested_module: String(data.interested_module || '').trim(),
    message: String(data.message || '').trim(),
    website: String(data.website || '').trim(),
    source_page: window.location.href,
    lead_type: leadType,
  };
  const res = await fetch('/api/public/website-leads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let msg = 'Talep gönderilemedi. Lütfen tekrar deneyin.';
    try {
      const body = await res.json();
      msg = body.detail || body.message || msg;
    } catch {
      // Keep the safe default message when the API body is not JSON.
    }
    throw new Error(msg);
  }
}

export default function PricingCTASection() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await submitLead(e.currentTarget, 'ciftlikweb_pro_demo');
      setFormSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Talep gönderilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" className="py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-4">
            CiftlikWeb Pro
          </span>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4">
            Paket yok. Tek ürün, net fiyat, net sözleşme.
          </h2>
          <p className="text-foreground-600 text-base md:text-lg max-w-3xl mx-auto">
            CiftlikWeb Pro; canlı hayvan, süt ve et stokları, kanatlı işletme, kurban, adaklık, cari, ödeme, havale, kesim, taşıma ve müşteri portalını birlikte sunar. Eksik modül veya yarım sistem olarak satılmaz.
          </p>
        </div>

        <div className="max-w-5xl mx-auto mb-6 rounded-2xl border border-accent-200 bg-accent-50 px-5 py-4 text-center text-sm leading-6 text-foreground-700 md:px-7 md:text-base">
          <i className="ri-shield-check-line mr-2 text-lg text-accent-700"></i>
          Kurban Bayramı karmaşasında unutulan tek bir kaporanın, yanlış kesilen bir hissenin veya atlanan bir bakiyenin maliyeti yazılım maliyetinden çok daha fazladır. CiftlikWeb, finansal kaçakları görünür hale getirerek ilk bayramındaki yatırımını karşılamayı hedefler.
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-6 items-start">
          <div className="bg-foreground-950 text-background-50 rounded-2xl p-6 md:p-8 shadow-sm border border-foreground-900">
            <div className="flex items-center justify-between gap-3 mb-6">
              <div>
                <p className="text-primary-200 text-sm font-semibold">Tek ürün modeli</p>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold mt-1">CiftlikWeb Pro</h3>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-2xl"></i>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <PriceBox label="Kurulum ve aktivasyon" value="70.000 TL + KDV" />
              <PriceBox label="Aylık kullanım" value="7.000 TL + KDV" />
              <PriceBox label="Sözleşme" value="12 ay taahhüt" />
              <PriceBox label="Cayma hakkı" value="İlk 30 gün" />
            </div>

            <div className="space-y-3 text-sm text-background-100">
              <CheckLine>Canlı hayvan, stok, satış, cari ve operasyon modülleri tek ürün modelinde birlikte çalışır.</CheckLine>
              <CheckLine>CiftlikWeb alt domaini, müşterinin kendi domaini veya bizim aldığımız/yönettiğimiz müşteri domaini seçilebilir.</CheckLine>
              <CheckLine>İlk 30 gün içinde cayma halinde kalan taahhüt iptal edilir; kurulum/aktivasyon bedeli iade edilmez.</CheckLine>
              <CheckLine>Veri aktarımı, yerinde eğitim, özel geliştirme ve ek domain/DNS yönetimleri ayrıca planlanır.</CheckLine>
            </div>
          </div>

          <div>
            {formSubmitted ? (
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-8 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 rounded-full mb-4">
                  <i className="ri-check-line text-primary-500 text-2xl"></i>
                </div>
                <h3 className="font-heading font-semibold text-xl text-foreground-950 mb-2">
                  Görüşme talebiniz alındı!
                </h3>
                <p className="text-foreground-600">
                  CiftlikWeb Pro kurulumu, domain seçimi ve canlı kullanım sürecini birlikte değerlendirmek için sizinle iletişime geçeceğiz.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-8">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                {error && <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                  <Field id="demo-ad" name="full_name" label="Ad Soyad" required placeholder="Adınız ve soyadınız" />
                  <Field id="demo-firma" name="company_name" label="Firma Adı" required placeholder="İşletmenizin adı" />
                  <Field id="demo-email" name="email" label="E-posta" type="email" placeholder="ornek@firma.com" />
                  <Field id="demo-telefon" name="phone" label="Telefon" type="tel" required placeholder="05XX XXX XX XX" />
                  <Field id="demo-sehir" name="city" label="Şehir" placeholder="İstanbul, Bursa, Ankara..." />
                  <Select id="demo-tur" name="business_type" label="İşletme Türü" options={BUSINESS_TYPES} />
                  <div className="sm:col-span-2">
                    <Select id="demo-modul" name="interested_module" label="Görüşme Konusu" options={INTEREST_AREAS} />
                  </div>
                </div>
                <div className="mb-6">
                  <label htmlFor="demo-mesaj" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    İşletmenizin mevcut sürecini kısaca anlatın
                  </label>
                  <textarea id="demo-mesaj" name="message" rows={3} maxLength={800} className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none" placeholder="Kaç hayvan, kaç kullanıcı, stok/ürün takibi, kurban dönemi yoğunluğu veya domain tercihi..."></textarea>
                </div>
                <button type="submit" disabled={submitting} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 text-sm md:text-base font-semibold rounded-md hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {submitting ? 'Gönderiliyor...' : 'Demo ve Kurulum Görüşmesi Talep Et'}
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceBox({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl border border-background-50/10 bg-background-50/5 p-4"><p className="text-xs text-background-200">{label}</p><p className="mt-1 font-heading font-bold text-lg text-background-50">{value}</p></div>;
}

function CheckLine({ children }: { children: ReactNode }) {
  return <div className="flex gap-2"><i className="ri-check-line text-primary-300 mt-0.5"></i><span>{children}</span></div>;
}

function Field({ id, name, label, type = 'text', required, placeholder }: { id: string; name: string; label: string; type?: string; required?: boolean; placeholder?: string }) {
  return <div><label htmlFor={id} className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</label><input id={id} name={name} type={type} required={required} className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all" placeholder={placeholder} /></div>;
}

function Select({ id, name, label, options }: { id: string; name: string; label: string; options: string[] }) {
  return <div><label htmlFor={id} className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</label><select id={id} name={name} className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"><option value="">Seçiniz</option>{options.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>;
}
